<template>
  <div class="pricing-container">
    <!-- Header -->
    <div class="pricing-header">
      <h1 class="pricing-title">选择适合你的创作引擎</h1>
      <p class="pricing-subtitle">灵活的「订阅 + 积分」模式，满足从尝鲜到专业生产的所有需求</p>
      
      <!-- Billing Toggle -->
      <div class="billing-toggle-wrapper">
        <span :class="{ 'active': !isAnnual }" @click="isAnnual = false">月付</span>
        <el-switch
          v-model="isAnnual"
          style="margin: 0 16px;"
          active-color="#7c3aed"
          inactive-color="#7c3aed"
        />
        <span :class="{ 'active': isAnnual }" @click="isAnnual = true">
          年付
          <el-tag size="small" type="success" effect="dark" class="discount-badge">省 40%</el-tag>
        </span>
      </div>
    </div>

    <!-- Subscription Plans Grid -->
    <div class="pricing-grid">
      <el-row :gutter="24" type="flex" justify="center">
        <!-- 免费体验 -->
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <div class="pricing-card free-tier">
            <div class="plan-header">
              <h3>免费体验</h3>
              <p class="plan-desc">适合想完整体验核心功能的新手</p>
            </div>
            <div class="plan-price">
              <span class="currency">¥</span>
              <span class="amount">0</span>
            </div>
            <div class="plan-points">
              <strong>30 积分</strong> / 每日发放 (当日有效)
            </div>
            <ul class="feature-list">
              <li><el-icon><Check /></el-icon> 视频最高 720p</li>
              <li><el-icon><Check /></el-icon> 最长支持 5 秒</li>
              <li class="disabled"><el-icon><Close /></el-icon> 带有平台水印</li>
              <li class="disabled"><el-icon><Close /></el-icon> 普通排队队列</li>
            </ul>
            <el-button class="plan-btn" plain round @click="handleAction('free')">免费开始</el-button>
          </div>
        </el-col>

        <!-- 入门版 Starter (Most Popular) -->
        <el-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="pricing-card starter-tier popular">
            <div class="popular-badge">最受欢迎</div>
            <div class="plan-header">
              <h3>入门版 Starter</h3>
              <p class="plan-desc">降低门槛，跑通个人视频创作</p>
            </div>
            <div class="plan-price">
              <span class="currency">¥</span>
              <span class="amount">{{ isAnnual ? '29' : '49' }}</span>
              <span class="period">/月</span>
            </div>
            <div v-if="!isAnnual" class="promo-text">限时特惠: 首月仅需 ¥19.9</div>
            <div v-if="isAnnual" class="promo-text">按年计费 (¥348/年)</div>
            <div class="plan-points">
              <strong>800 积分</strong> / 每月发放
            </div>
            <ul class="feature-list">
              <li><el-icon><Check /></el-icon> <strong>无水印</strong>导出</li>
              <li><el-icon><Check /></el-icon> 视频最高 1080p</li>
              <li><el-icon><Check /></el-icon> 最长支持 10 秒</li>
              <li><el-icon><Check /></el-icon> 标准排队队列</li>
              <li><el-icon><Check /></el-icon> 解锁专属提示词库</li>
            </ul>
            <el-button class="plan-btn btn-primary-purple" round @click="openPaymentModal('starter')">立即开始</el-button>
          </div>
        </el-col>

        <!-- 专业版 Pro -->
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <div class="pricing-card pro-tier">
            <div class="plan-header">
              <h3>专业版 Pro</h3>
              <p class="plan-desc">重度视频创作者及小团队首选</p>
            </div>
            <div class="plan-price">
              <span class="currency">¥</span>
              <span class="amount">{{ isAnnual ? '79' : '129' }}</span>
              <span class="period">/月</span>
            </div>
            <div v-if="isAnnual" class="promo-text">按年计费 (¥948/年)</div>
            <div v-else class="promo-text-empty">&nbsp;</div>
            <div class="plan-points">
              <strong>3000 积分</strong> / 每月发放
            </div>
            <ul class="feature-list">
              <li><el-icon><Check /></el-icon> 包含入门版所有权益</li>
              <li><el-icon><Check /></el-icon> 最长支持 30 秒</li>
              <li><el-icon><Check /></el-icon> <strong>优先排队</strong>队列</li>
              <li><el-icon><Check /></el-icon> 故事板与画面续写</li>
              <li><el-icon><Check /></el-icon> 批量生成 (享 8 折优惠)</li>
            </ul>
            <el-button class="plan-btn" plain round @click="openPaymentModal('pro')">立即开始</el-button>
          </div>
        </el-col>

        <!-- 尊享版 Enterprise (Anchor) -->
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <div class="pricing-card enterprise-tier">
            <div class="plan-header">
              <h3>尊享版 Enterprise</h3>
              <p class="plan-desc">极致性能，服务深度企业用户</p>
            </div>
            <div class="plan-price">
              <span class="currency">¥</span>
              <span class="amount">{{ isAnnual ? '199' : '299' }}</span>
              <span class="period">/月</span>
            </div>
            <div v-if="isAnnual" class="promo-text">按年计费 (¥2,388/年)</div>
            <div v-else class="promo-text-empty">&nbsp;</div>
            <div class="plan-points">
              <strong>10000 积分</strong> / 每月发放
            </div>
            <ul class="feature-list">
              <li><el-icon><Check /></el-icon> 包含专业版所有权益</li>
              <li><el-icon><Check /></el-icon> 视频最高 <strong>4K 画质</strong></li>
              <li><el-icon><Check /></el-icon> 最长支持 60 秒</li>
              <li><el-icon><Check /></el-icon> 极速专属列队</li>
              <li><el-icon><Check /></el-icon> <strong>API 接入</strong>及专属客服</li>
            </ul>
            <el-button class="plan-btn" plain round @click="openPaymentModal('enterprise')">联系我们</el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Top-up Packages Section -->
    <div class="topup-section">
      <h2 class="section-title">积分加油包 (按量付费)</h2>
      <p class="section-subtitle">积分永久有效，灵活补充，满足突发创作需求</p>
      
      <el-row :gutter="20" class="topup-grid">
        <el-col :xs="12" :sm="6" v-for="pkg in topupPackages" :key="pkg.id">
          <div class="topup-card" @click="openPaymentModal('topup', pkg)">
            <div class="topup-points">{{ pkg.points }} <span class="points-label">积分</span></div>
            <div class="topup-price">¥{{ pkg.price }}</div>
            <div class="topup-unit-price">约 ¥{{ pkg.unitPrice }}/积分</div>
            <el-button class="topup-btn" plain size="small" round>购买</el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <div class="pricing-footer">
      <p class="guarantee-text"><el-icon><DocumentChecked /></el-icon> 所有付费方案均支持开具增值税专用发票</p>
      <p class="guarantee-text"><el-icon><Lock /></el-icon> 首次付费用户专享 7 天无理由全额退款保障 (未使用超过 100 积分)</p>
    </div>

    <!-- Alipay Payment Modal -->
    <el-dialog
      v-model="paymentVisible"
      title="订单支付"
      width="400px"
      center
      class="payment-dialog"
      :close-on-click-modal="false"
    >
      <div class="payment-content">
        <div class="order-summary">
          <div class="order-title">{{ currentOrder?.title }}</div>
          <div class="order-price">¥<span>{{ currentOrder?.price }}</span></div>
          <div v-if="currentOrder?.promo" class="order-promo">
            <el-tag type="danger" size="small" effect="plain">{{ currentOrder?.promo }}</el-tag>
          </div>
        </div>

        <el-divider border-style="dashed" />

        <div class="payment-method-header">请使用支付宝扫码支付</div>
        <div class="qrcode-wrapper">
          <div class="qrcode-border">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Alipay_logo.svg" class="alipay-logo" alt="Alipay" />
            <!-- Using a mock QR code from an API -->
            <img :src="`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=mock_alipay_order_${currentOrder?.id}`" class="qrcode-img" alt="Payment QR" />
          </div>
        </div>

        <div class="payment-tips">
          <el-icon><Warning /></el-icon> 支付成功后积分将自动到账
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="paymentVisible = false">暂不支付</el-button>
          <el-button type="primary" :loading="paymentLoading" @click="simulatePaymentSuccess">
            我已支付
          </el-button>
        </span>
      </template>
    </el-dialog>

  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Check, Close, DocumentChecked, Lock, Warning } from '@element-plus/icons-vue'
import { ElMessage, ElLoading } from 'element-plus'
import api from '@/services/authService'

const isAnnual = ref(true)
const paymentVisible = ref(false)
const paymentLoading = ref(false)
const currentOrder = ref<any>(null)

const topupPackages = [
  { id: 't1', title: '体验加油包', points: 300, price: 29, unitPrice: '0.097' },
  { id: 't2', title: '标准加油包', points: 1200, price: 99, unitPrice: '0.083' },
  { id: 't3', title: '进阶加油包', points: 3500, price: 249, unitPrice: '0.071' },
  { id: 't4', title: '企业加油包', points: 12000, price: 699, unitPrice: '0.058' }
]

const handleAction = (type: string) => {
  if (type === 'free') {
    ElMessage.success('您已激活免费体验，每日可获 30 积分。')
  }
}

const openPaymentModal = async (type: string, pkg: any = null) => {
  let package_id = 'pkg_basic'
  
  if (type === 'starter') {
    package_id = 'pkg_basic'
  } else if (type === 'pro') {
    package_id = 'pkg_pro'
  } else if (type === 'enterprise') {
    package_id = 'pkg_max'
  } else if (type === 'topup' && pkg) {
    if (pkg.id === 't1') package_id = 'pkg_basic'
    else if (pkg.id === 't2') package_id = 'pkg_pro'
    else package_id = 'pkg_max'
  }
  
  const loading = ElLoading.service({
    lock: true,
    text: '正在拉起支付宝...',
    background: 'rgba(0, 0, 0, 0.7)',
  })
  
  try {
    const res = await api.post('/api/payment/alipay/create_order', { package_id })
    if (res.data.success) {
      // Create a form element to submit the HTML content if it's a form string, 
      // or directly redirect if it's a URL.
      if (res.data.pay_url.includes('<form')) {
        const div = document.createElement('div')
        div.innerHTML = res.data.pay_url
        document.body.appendChild(div)
        document.forms[0].submit()
      } else {
        window.location.href = res.data.pay_url
      }
    }
  } catch (error: any) {
    console.error('拉起支付失败:', error)
    if (error.response?.status === 401) {
      ElMessage.warning('请先登录后再进行充值')
    } else {
      ElMessage.error(error.response?.data?.detail || '创建订单失败')
    }
  } finally {
    loading.close()
  }
}

const simulatePaymentSuccess = () => {
  paymentLoading.value = true
  setTimeout(() => {
    paymentLoading.value = false
    paymentVisible.value = false
    ElMessage.success({
      message: '支付成功！积分已到账。',
      duration: 3000
    })
  }, 1500)
}
</script>

<style scoped>
.pricing-container {
  padding: 40px 24px;
  background-color: #f8fafc;
  min-height: 100%;
}

.pricing-header {
  text-align: center;
  margin-bottom: 48px;
}

.pricing-title {
  font-size: 36px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 12px;
}

.pricing-subtitle {
  font-size: 16px;
  color: #64748b;
  margin-bottom: 32px;
}

.billing-toggle-wrapper {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 8px 16px;
  border-radius: 100px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.billing-toggle-wrapper span {
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.billing-toggle-wrapper span.active {
  color: #1e293b;
  font-weight: 700;
}

.discount-badge {
  background: #10b981;
  border-color: #10b981;
  font-weight: bold;
}

/* Pricing Grid */
.pricing-grid {
  margin-bottom: 60px;
}

.pricing-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 32px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.08);
}

/* Popular Tier Special Styling */
.starter-tier.popular {
  border: 2px solid #7c3aed;
  transform: scale(1.05);
  box-shadow: 0 16px 32px rgba(124, 58, 237, 0.15);
  z-index: 10;
}

.starter-tier.popular:hover {
  transform: scale(1.05) translateY(-4px);
}

.popular-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  padding: 4px 16px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 10px rgba(124, 58, 237, 0.3);
}

.plan-header {
  margin-bottom: 24px;
}

.plan-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.plan-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  min-height: 40px;
}

.plan-price {
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
}

.plan-price .currency {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.plan-price .amount {
  font-size: 48px;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -1px;
}

.plan-price .period {
  font-size: 16px;
  color: #64748b;
  margin-left: 4px;
}

.promo-text {
  font-size: 13px;
  color: #ef4444;
  font-weight: 600;
  margin-bottom: 16px;
  height: 20px;
}

.promo-text-empty {
  height: 20px;
  margin-bottom: 16px;
}

.plan-points {
  background: #f1f5f9;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #334155;
  margin-bottom: 24px;
  text-align: center;
}

.plan-points strong {
  color: #7c3aed;
  font-size: 16px;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  flex: 1;
}

.feature-list li {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #334155;
  margin-bottom: 16px;
  gap: 10px;
}

.feature-list li .el-icon {
  color: #10b981;
  font-size: 16px;
  font-weight: bold;
}

.feature-list li.disabled {
  color: #94a3b8;
}

.feature-list li.disabled .el-icon {
  color: #cbd5e1;
}

.plan-btn {
  width: 100%;
  padding: 20px 0;
  font-size: 16px;
  font-weight: 600;
}

.btn-primary-purple {
  background: #7c3aed;
  border-color: #7c3aed;
  color: white;
}

.btn-primary-purple:hover {
  background: #6d28d9;
  border-color: #6d28d9;
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.25);
}

/* Top-up Section */
.topup-section {
  max-width: 1000px;
  margin: 0 auto 60px;
  text-align: center;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 15px;
  color: #64748b;
  margin-bottom: 32px;
}

.topup-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topup-card:hover {
  border-color: #7c3aed;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.1);
  transform: translateY(-2px);
}

.topup-points {
  font-size: 28px;
  font-weight: 800;
  color: #7c3aed;
  margin-bottom: 8px;
}

.points-label {
  font-size: 14px;
  font-weight: normal;
  color: #64748b;
}

.topup-price {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.topup-unit-price {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.topup-btn {
  width: 100%;
}

/* Footer */
.pricing-footer {
  text-align: center;
  padding-top: 32px;
  border-top: 1px solid #e2e8f0;
}

.guarantee-text {
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* Payment Dialog */
.payment-dialog .el-dialog__header {
  border-bottom: 1px solid #f1f5f9;
  margin-right: 0;
}

.payment-content {
  text-align: center;
  padding: 10px 0;
}

.order-title {
  font-size: 16px;
  color: #334155;
  margin-bottom: 8px;
}

.order-price {
  font-size: 32px;
  font-weight: 800;
  color: #ef4444;
}

.order-promo {
  margin-top: 8px;
}

.payment-method-header {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.qrcode-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.qrcode-border {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  position: relative;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);
}

.alipay-logo {
  height: 24px;
  margin-bottom: 12px;
}

.qrcode-img {
  width: 180px;
  height: 180px;
  display: block;
}

.payment-tips {
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@media (max-width: 1200px) {
  .starter-tier.popular {
    transform: none;
    z-index: 1;
    margin-top: 16px;
  }
  .starter-tier.popular:hover {
    transform: translateY(-4px);
  }
  .pricing-grid {
    margin-bottom: 40px;
  }
}

@media (max-width: 768px) {
  .pricing-col {
    margin-bottom: 24px;
  }
  .starter-tier.popular {
    margin-top: 24px;
    margin-bottom: 8px;
  }
}
</style>
